/**
 * Derives a stable, low-cardinality `pageType` identifier from a Next.js
 * pathname. Used by the Segment client beacon so SEO compliance reports can
 * slice violations by surface (e.g. all `/ppc/trusted-home-care/...` pages)
 * without maintaining a parallel allow-list per page.
 *
 * The identifier is intentionally coarse: it groups pages by structural
 * surface, not by individual URL. New surfaces can be added by extending
 * `PAGE_TYPE_PATTERNS` below; matching is first-rule-wins.
 */

type PageTypePattern = {
  /** Stable identifier emitted as the `pageType` Segment property. */
  pageType: string;
  /** Regex matched against the normalized pathname (no query/hash). */
  pattern: RegExp;
};

/**
 * Surface taxonomy. Hyphenated, surface-prefixed (`ppc-*`, `site-builder-*`,
 * `franchise-*`, `legacy-*`). Order matters: more specific patterns must
 * appear first so the broader fallbacks don't shadow them.
 */
const PAGE_TYPE_PATTERNS: readonly PageTypePattern[] = [
  // hi-care PPC trusted-home-care landing surfaces
  {
    pageType: "ppc-trusted-home-care-local",
    pattern: /^\/ppc\/trusted-home-care\/[^/]+\/?$/,
  },
  {
    pageType: "ppc-trusted-home-care-national",
    pattern: /^\/ppc\/trusted-home-care\/?$/,
  },
  // hi-www routes that pass through the restricted-segment override
  // (e.g. office-scoped `/.../ppc/...` Site Builder URLs).
  {
    pageType: "ppc-site-builder",
    pattern: /(^|\/)ppc(\/|$)/,
  },

  // hi-care franchise-opportunities surfaces
  {
    pageType: "franchise-opportunities-request-info",
    pattern: /^\/franchise-opportunities\/request-info\/?$/,
  },
  {
    pageType: "franchise-opportunities",
    pattern: /^\/franchise-opportunities(\/.*)?$/,
  },

  // hi-care referral surface
  {
    pageType: "referral",
    pattern: /^\/referral\/?$/,
  },

  // Legacy franchise-office home-page URLs
  // (e.g. /home-care/usa/ne/omaha/100/)
  {
    pageType: "legacy-office-home-page",
    pattern: /^\/home-care\/(?:usa|can)\/[^/]+\/[^/]+\/[0-9]+\/?$/i,
  },

  // Site Builder office-rooted routes
  {
    pageType: "site-builder-office-route",
    pattern: /^\/home-care\/(?:usa|can)\//i,
  },

  // Default: any other route under hi-www / hi-care that isn't matched above
  // is treated as a generic Site Builder route.
  {
    pageType: "site-builder-route-page",
    pattern: /^\//,
  },
];

const FALLBACK_PAGE_TYPE = "unknown" as const;

function normalizePathname(pathname: string | null | undefined): string {
  if (!pathname) {
    return "";
  }

  // Strip query string / hash if a caller accidentally passes them through.
  const queryStart = pathname.indexOf("?");
  const stripped = queryStart === -1 ? pathname : pathname.slice(0, queryStart);
  const hashStart = stripped.indexOf("#");

  return hashStart === -1 ? stripped : stripped.slice(0, hashStart);
}

function getPageType(pathname: string | null | undefined): string {
  const normalized = normalizePathname(pathname);

  if (!normalized) {
    return FALLBACK_PAGE_TYPE;
  }

  for (const { pageType, pattern } of PAGE_TYPE_PATTERNS) {
    if (pattern.test(normalized)) {
      return pageType;
    }
  }

  return FALLBACK_PAGE_TYPE;
}

export { FALLBACK_PAGE_TYPE, getPageType };
